/**
 * MSI / Plessey symbology — digit-to-bar-pattern lookup.
 *
 * Each decimal digit is encoded as 4 BCD bits, MSB first. Each bit is
 * one bar + one space:
 *   bit 1 → wide bar (2 modules) + narrow space (1 module)
 *   bit 0 → narrow bar (1 module) + wide space (2 modules)
 * So each digit is 8 elements (4 bars + 4 spaces) totaling 12 modules.
 *
 * Start: a single wide bar (= the bar portion of bit 1)
 * Stop:  a narrow bar followed by a wide bar (= the bar portion of bit 0
 *        then the bar portion of bit 1), so the symbol always ends on a
 *        bar. The trailing wide bar is the actual stop marker; the
 *        preceding narrow bar is the carryover from the last digit's
 *        last bit's bar half — but for safety we treat the last two
 *        elements as the stop pattern.
 *
 * Note on variants: some sources flip which width represents "1" vs "0".
 * I'm using "1 = wide bar + narrow space" because that's the convention
 * in most current barcode-reference materials (e.g. Honeywell Barcode
 * Reference Guide, GS1 General Specifications appendices on legacy
 * symbologies). If field tests disagree, swap by changing BIT_PATTERNS
 * — everything else flows from this single source of truth.
 */

import type { ChecksumVariant } from './types';

/** Module widths for each bit value: [barWidth, spaceWidth]. */
export const BIT_PATTERNS: Record<0 | 1, [number, number]> = {
  0: [1, 2], // narrow bar + wide space
  1: [2, 1], // wide bar  + narrow space
};

/** Module width treated as "narrow". */
export const NARROW = 1;
/** Module width treated as "wide". */
export const WIDE = 2;

/**
 * Pre-computed digit → 8-element module-width array (bar, space, bar, ...).
 * Generated from BIT_PATTERNS at module load (cheap, deterministic).
 * Each entry has length 8 (4 bars + 4 spaces); total module width = 12.
 */
export const DIGIT_PATTERNS: readonly (readonly number[])[] = (() => {
  const out: number[][] = [];
  for (let d = 0; d < 10; d++) {
    const bits = [
      (d >> 3) & 1, // MSB first
      (d >> 2) & 1,
      (d >> 1) & 1,
      d & 1,
    ] as Array<0 | 1>;
    const pattern: number[] = [];
    for (const b of bits) {
      const [bar, space] = BIT_PATTERNS[b];
      pattern.push(bar, space);
    }
    out.push(pattern);
  }
  return out.map((p) => Object.freeze(p)) as readonly (readonly number[])[];
})();

/** Start pattern in modules: one wide bar with no preceding space. */
export const START_PATTERN: readonly number[] = Object.freeze([WIDE]);

/**
 * Stop pattern in modules. The classic MSI stop is "narrow bar + wide bar".
 * After the last digit's final space, we expect this pair. The decoder
 * also accepts a single narrow bar (some printings drop the trailing
 * wide bar — common on damaged tags) when the rest of the decode is
 * otherwise clean.
 */
export const STOP_PATTERN: readonly number[] = Object.freeze([NARROW, WIDE]);

/** Default checksum-variant preference (used when caller doesn't override). */
export const DEFAULT_CHECKSUM_ORDER: readonly ChecksumVariant[] = Object.freeze([
  'mod10',
  'mod11',
  'mod1010',
  'mod1110',
  'none',
]);
